/**
 * Recipient-side scratch-share viewer. URL shape: /<id> where <id> is a
 * 10-char nanoid from POST /api/scratch-share. Single-route page — any
 * unknown path renders the same component which shows a "not found"
 * state.
 *
 * UX (2026-06-09 redesign):
 *   - Header bar: Linify wordmark (left) + "Get App" pill (right). The
 *     wordmark is the production sky-theme variant (matches the
 *     iOS gallery), shipped as a static SVG from /brand/linify-sky.svg.
 *     Header replaces the previous fixed bottom CTA — recipients see
 *     branding immediately on load instead of after scroll.
 *
 *   - Body: max-width clamp keeps the page in a mobile-feeling column
 *     on desktop (~ 480 px) so the foil card never blows up into a
 *     desktop-grid layout.
 *
 *   - Below the card:
 *       1. Initially → "Scratch off to reveal" subtle hint
 *       2. Once user starts scratching → finger animation inside the
 *          card fades out (handled by ScratchPlayer's hintDismissed)
 *       3. Once scratching reaches the shine threshold (~98 %) →
 *          ScratchPlayer fires onScratchComplete, which:
 *            - fades out the "Scratch off to reveal" hint
 *            - fades in a primary "Make Your Own in Linify" CTA in the
 *              same vertical slot
 *
 *   - Bootstrap data still flows from `window.__SCRATCH_SHARE__` per
 *     the prior layout-shift-free first-paint architecture; legacy
 *     fallback to `/api/scratch-share/:id` stays in place.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ScratchPlayer } from './ScratchPlayer';
import { Confetti } from './Confetti';

// Build-stamp written into the runtime so Vite's content-hash for this
// bundle changes whenever we bump it. We need a content-affecting (not
// just comment) change to force a new `/assets/index-<hash>.js`
// filename; minified comments get dropped and don't shift the hash.
// Bump when we need browsers to fetch a fresh bundle.
const SCRATCH_WEB_BUILD = '2026-09-09-open-in-scratch-preview';
(globalThis as { SCRATCH_WEB_BUILD?: string }).SCRATCH_WEB_BUILD = SCRATCH_WEB_BUILD;

const APP_STORE_URL =
  'https://apps.apple.com/us/app/coloring-pages-by-linify/id6763802132';

interface ShareData {
  id: string;
  createdAt: string;
  outlineUrl: string;
  colorUrl: string;
  /** Outline pixel dims. Null for legacy rows (pre-migration #24); the
   *  card falls back to 1:1 aspect when missing. */
  width: number | null;
  height: number | null;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'loaded'; data: ShareData }
  | { kind: 'not_found' }
  | { kind: 'expired' }
  | { kind: 'error'; message: string };

/** Shape of the Pages-Function-injected bootstrap. Optional fields because
 *  the function emits null for anything it couldn't resolve. */
interface BootstrapShare {
  id?: string | null;
  createdAt?: string | null;
  outlineUrl?: string | null;
  colorUrl?: string | null;
  previewUrl?: string | null;
  width?: number | null;
  height?: number | null;
}

function parseIdFromPath(pathname: string): string | null {
  // Expect '/<id>' or '/<id>/...'; drop leading slash and any trailing junk
  // a CDN might append (trailing slash, query, fragment).
  const trimmed = pathname.replace(/^\/+/, '').split(/[/?#]/)[0];
  if (/^[2-9a-zA-Z]{10,12}$/.test(trimmed)) return trimmed;
  return null;
}

/** Pull the Pages-Function bootstrap if present and well-formed. */
function readBootstrap(): ShareData | null {
  const w = globalThis as unknown as { __SCRATCH_SHARE__?: BootstrapShare };
  const b = w.__SCRATCH_SHARE__;
  if (!b || !b.id || !b.outlineUrl || !b.colorUrl) return null;
  return {
    id: b.id,
    createdAt: b.createdAt ?? '',
    outlineUrl: b.outlineUrl,
    colorUrl: b.colorUrl,
    width: typeof b.width === 'number' ? b.width : null,
    height: typeof b.height === 'number' ? b.height : null,
  };
}

/** Linify wordmark — centered, sits ABOVE the card. Tapping it normally
 *  goes to the App Store. In capture mode (?m=1 / ?m=2) an `onTap` handler is
 *  passed instead: the tap becomes the capture control (lock scroll/zoom,
 *  then reset) and navigation is suppressed — the App Store CTA there is
 *  the separate badge below. The brand-mark is the FIRST child of
 *  `.stage`'s flex column, immediately above the card with flex
 *  `gap: 14px` between them. */
function BrandMark({ onTap }: { onTap?: () => void }) {
  return (
    <a
      className="brand-mark"
      href={APP_STORE_URL}
      aria-label="Linify — Turn Your Photos Into Coloring Pages. Get on the App Store"
      onClick={onTap ? (e) => { e.preventDefault(); onTap(); } : undefined}
    >
      <span className="brand-mark-row">
        {/* App icon in a rounded-rect, inline to the LEFT of the wordmark. */}
        <img
          src="/apple-touch-icon.png"
          alt=""
          aria-hidden="true"
          className="brand-mark-appicon"
          draggable={false}
        />
        <img
          src="/brand/linify-sky.svg"
          alt="Linify"
          className="brand-mark-logo"
          draggable={false}
        />
      </span>
      {/* Tagline directly under the wordmark — the value prop before the
        * recipient touches the foil. One line covering both hooks
        * (coloring pages + scratch-offs); in ?m=1 it's also the only
        * place the "make your own" idea appears, since the post-reveal
        * Make-Your-Own pill is removed there. Same anchor tag, so a tap
        * on either logo or tagline routes to the App Store. */}
      <span className="brand-mark-tagline">Turn Photos Into Coloring Pages &amp; Scratch-Offs</span>
    </a>
  );
}

/** Get App pill — sits BELOW the card. Same visual style as the prior
 *  header-cta (sky-blue gradient + download icon) so installed-app
 *  recognition is preserved across the layout move. */
function GetAppPill() {
  return (
    <a className="get-app-cta" href={APP_STORE_URL}>
      <svg
        className="get-app-cta-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      <span>Get App</span>
    </a>
  );
}

/** Dev-mode detection — true on CF Pages preview deploys
 *  (`*.pages.dev`) AND when the URL carries a `?dev` query param. The
 *  pages.dev branch is the autopilot one so we don't have to remember
 *  to append the query manually when poking at dev.linify-scratch
 *  .pages.dev; the query branch is the escape hatch for testing the
 *  same animation locally or on prod if we ever need to. Production
 *  recipient shares (on the eventual custom domain) hit neither
 *  branch and so never see the dev tools. Evaluated once at module
 *  load — won't react to URL changes mid-session, but the share
 *  viewer is single-route so that doesn't matter. */
const IS_DEV_HOST =
  typeof window !== 'undefined' &&
  (window.location.hostname.endsWith('.pages.dev') ||
    window.location.search.includes('dev'));

/** Minimal "capture" mode — enabled by `?m=1`. A cleaner layout for
 *  recording ad videos + taking screen captures (NOT a public-facing
 *  variant): the "Get App" pill and the post-reveal "Make Your Own
 *  Scratch-Off" pill are both suppressed, and the official Apple
 *  "Download on the App Store" badge is shown under the brand tagline
 *  as the single CTA. Read once at module load — the viewer is single-
 *  route, so it never needs to react to a mid-session URL change.
 *
 *  Adding `.minimal` to <html> (before React's first render, so no
 *  layout-shift flash) lets styles.css re-budget `--card-chrome` for
 *  the missing pills → a slightly larger, better-centered card for
 *  capture. Guarded on `document` for non-DOM eval safety. */
const CAPTURE_PARAM =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('m')
    : null;

const IS_MINIMAL = CAPTURE_PARAM === '1';

/** Marketing capture mode — `?m=2`. Same page and same suppressions as
 *  ?m=1; only the composition differs, and all of it lives in CSS under
 *  `html.marketing` (see the block comment there for the full list):
 *    1. the App Store badge is 36px and tinted, still centred in flow
 *       directly under the tagline;
 *    2. the whole header sits lower down the page;
 *    3. the card fills the screen width and is centred in the VIEWPORT
 *       rather than in the space left under the header. */
const IS_MARKETING = CAPTURE_PARAM === '2';

/** True in EITHER capture mode. Everything that means "this is a capture,
 *  not a recipient view" keys off this — pill suppression, the Apple
 *  badge, the tap-to-lock control, the lower scratch threshold — so the
 *  two modes can't drift apart on behaviour. Only layout differs, and
 *  that lives entirely in CSS. */
const IS_CAPTURE = IS_MINIMAL || IS_MARKETING;

/** True when this page is already running inside the Scratch Preview app
 *  (apps/fullscreen-viewer) rather than a browser. That app is a WKWebView
 *  host used to capture marketing frames with no status bar and no browser
 *  chrome; it tags its User-Agent via `applicationNameForUserAgent`.
 *
 *  We ask the APP to identify itself rather than trying to recognise Safari,
 *  because "is this Safari" has no reliable answer — WKWebView's default UA
 *  is deliberately near-identical to Mobile Safari's. Sniffing for the thing
 *  we control is exact; sniffing for the thing we don't is guesswork.
 *
 *  Failing OPEN is the safe direction: if the tag ever changes and this reads
 *  false inside the app, the button shows there and tapping it re-opens the
 *  page we are already on — harmless. The reverse (hiding the button in
 *  Safari) would remove the only way in. */
const IN_PREVIEW_APP =
  typeof navigator !== 'undefined' && /LinifyScratchPreview/.test(navigator.userAgent);

if (typeof document !== 'undefined') {
  if (IS_MINIMAL) document.documentElement.classList.add('minimal');
  if (IS_MARKETING) document.documentElement.classList.add('marketing');
}

/** In ?m=1 capture mode, fire the scratch finale (reveal + confetti)
 *  after this fraction of the card is scratched, vs the default 0.985,
 *  so ad-capture reveals need less hand-scratching. */
const MINIMAL_SCRATCH_THRESHOLD = 0.97;

/** ?m=1 capture control — lock/unlock page scroll + pinch-zoom WITHOUT
 *  moving the current scroll/zoom. We only block the browser's default
 *  gesture actions via non-passive listeners; scroll offset + zoom level
 *  are never touched, so unlocking leaves the framing exactly where the
 *  user set it. Touches on the foil card are left alone so scratching
 *  (pointer events + the canvas's own touch-action:none) still works;
 *  multi-touch anywhere is blocked so a stray pinch can't zoom mid-take.
 *  `gesturestart`/`gesturechange` are the iOS-Safari pinch hooks — Safari
 *  ignores `user-scalable=no`, so these are the real zoom lock. */
function onLockedTouchMove(e: TouchEvent) {
  if (e.touches.length > 1) { e.preventDefault(); return; }
  const t = e.target as Element | null;
  if (!t || !t.closest('.card')) e.preventDefault();
}
function onLockedGesture(e: Event) { e.preventDefault(); }
function addCaptureLock() {
  document.addEventListener('touchmove', onLockedTouchMove, { passive: false, capture: true });
  document.addEventListener('gesturestart', onLockedGesture, { passive: false });
  document.addEventListener('gesturechange', onLockedGesture, { passive: false });
}
function removeCaptureLock() {
  document.removeEventListener('touchmove', onLockedTouchMove, { capture: true });
  document.removeEventListener('gesturestart', onLockedGesture);
  document.removeEventListener('gesturechange', onLockedGesture);
}

/** Official Apple "Download on the App Store" badge. Shown only in the
 *  capture modes, centred directly under the brand tagline in both —
 *  ?m=2 only resizes and re-tints it, purely in CSS
 *  (`html.marketing .app-store-badge`). It is a
 *  SIBLING of BrandMark's anchor, never nested inside it: Apple's
 *  badge must itself link to the App Store product page, and a nested
 *  <a> is invalid HTML. Artwork is Apple's official SVG at
 *  public/brand/app-store-badge.svg (US/UK black, 119.66×40). */
function AppStoreBadge() {
  return (
    <a
      className="app-store-badge"
      href={APP_STORE_URL}
      aria-label="Download on the App Store"
    >
      <img
        // ?m=2 swaps in a copy tinted to the page's own dark tone (see the
        // header comment in that file). Same artwork, one `fill` different.
        src={IS_MARKETING ? '/brand/app-store-badge-tinted.svg' : '/brand/app-store-badge.svg'}
        alt=""
        aria-hidden="true"
        draggable={false}
      />
    </a>
  );
}

/** Top-of-page brand block, used by every load state so the header is
 *  identical across loading / loaded / error. In minimal capture mode
 *  the App Store badge follows the brand mark (i.e. sits under the
 *  tagline); otherwise it's just the brand mark. */
function TopBrand({ onTap }: { onTap?: () => void }) {
  return (
    <>
      <BrandMark onTap={onTap} />
      {IS_CAPTURE && <AppStoreBadge />}
    </>
  );
}

/** "Open in Scratch Preview" — hands this exact URL to the internal capture
 *  app (apps/fullscreen-viewer) over its custom `linifyscratchpreview://`
 *  scheme, which shows the page with no status bar and no browser chrome.
 *
 *  Gated on ?m=2 AND on not already being in that app:
 *    - ?m=2 is the marketing-capture layout, which only we ever load, so
 *      this is what keeps the button off a real recipient's screen. It is
 *      the ONLY thing keeping it off — do not render it in any other mode.
 *    - inside the app the button is a no-op loop, so it is hidden there.
 *
 *  It can never contaminate a capture: captures are taken inside the app,
 *  where IN_PREVIEW_APP is true and this returns null.
 *
 *  `position: fixed` (see styles.css) keeps it out of `.stage`, whose
 *  `overflow: hidden` and card-height maths would otherwise have to account
 *  for it. Rendered for every state — loading, error, loaded — because
 *  handing a broken share to the app is a legitimate thing to want to do.
 *
 *  If the app isn't installed, Safari reports an invalid address. That is
 *  the correct outcome for a link only we can follow. */
function OpenInPreviewApp() {
  if (!IS_MARKETING || IN_PREVIEW_APP) return null;
  const href = `linifyscratchpreview://open?u=${encodeURIComponent(window.location.href)}`;
  return (
    <a className="open-in-preview" href={href}>
      Open in Scratch Preview
    </a>
  );
}

/** Below-card "Get App" CTA. Suppressed in minimal capture mode, where
 *  the App Store badge under the tagline is the sole install CTA. */
function BottomCta() {
  return IS_CAPTURE ? null : <GetAppPill />;
}

/** Auto-finish — when true, ScratchPlayer programmatically clears
 *  the last 1.5% of the foil the moment the user crosses the 98.5%
 *  threshold, so the reveal moment is clean (no leftover foil
 *  patches at the rounded corners + brush-edge stragglers).
 *
 *  Promoted from a testing flag to default-on 2026-06-09 (user
 *  preferred the clean reveal in dev iteration). Kept as a runtime
 *  constant rather than removing the prop so a future opt-out is
 *  a one-line change here. To force OFF for comparison testing,
 *  flip this to `false` AND redeploy — no URL toggle is wired up
 *  for the off state since recipients won't need to switch. */
const IS_AUTO_FINISH = true;

/**
 * A random id for this browser, so the sender can be told how many DISTINCT
 * people opened their share rather than how many times the page was loaded.
 *
 * Deliberately random and client-generated — no IP, no fingerprint, nothing
 * derived from the device. Stored in localStorage, so a recipient can clear it
 * like any cookie, and it identifies nothing beyond "same browser as before".
 * Recipients are not app users and never agreed to anything, so this is the
 * least we can store and still answer the question.
 *
 * Failures are swallowed: Safari private mode and storage-blocked contexts
 * throw on localStorage access. Those viewers count as a fresh id each time,
 * which overstates unique viewers slightly rather than breaking the page.
 */
function viewerId(): string {
  const KEY = 'linify:vid';
  try {
    const existing = localStorage.getItem(KEY);
    if (existing) return existing;
    const fresh =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    return 'unavailable';
  }
}

/**
 * Shares this browser has already reported, so each one is reported once and
 * never again. Ten opens of the same page cost one request and one event.
 *
 * A list of ids, not a tally: nothing consumes a count above one — it is
 * never sent and never shown — so storing one would be inventing data with no
 * reader. An array rather than an object because share ids come from an
 * alphabet that includes digits, and an all-digit id would become an
 * integer-like object key, silently reordering entries and breaking the trim.
 *
 * Storage failures are swallowed: `hasReported` says no and `markReported`
 * does nothing, so a private-mode browser reports on every load. Those
 * carry no viewer id, so they land in the "unattributed" bucket and never
 * inflate the viewer count — over-reporting there beats losing the view.
 */
const REPORTED_KEY = 'linify:reported';
/** Plenty for a recipient; bounds growth if someone opens hundreds. */
const REPORTED_CAP = 50;

function readReported(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(REPORTED_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function hasReported(shareId: string): boolean {
  return readReported().includes(shareId);
}

function markReported(shareId: string): void {
  try {
    const next = readReported().filter((v) => v !== shareId);
    next.push(shareId);
    localStorage.setItem(REPORTED_KEY, JSON.stringify(next.slice(-REPORTED_CAP)));
  } catch {
    /* storage blocked — this browser reports every load, by design */
  }
}

export function App() {
  // Read the Pages-Function bootstrap synchronously during the first
  // render — initial state becomes 'loaded' directly, skipping the
  // "Loading…" frame entirely on the happy path.
  const [state, setState] = useState<LoadState>(() => {
    const boot = readBootstrap();
    if (boot) return { kind: 'loaded', data: boot };
    return { kind: 'loading' };
  });

  // ScratchPlayer signals these at most once per mount.
  const [scratchComplete, setScratchComplete] = useState(false);

  const handleScratchComplete = useCallback(() => {
    setScratchComplete(true);
  }, []);

  // ── ?m=1 capture control (Linify-mark tap) ───────────────────────────
  // Tap 1: lock page scroll + pinch-zoom so scratching can't shift the
  //   framing mid-take. Tap 2: reset the scratch + reveal state COMPLETELY
  //   (bump resetKey → ScratchPlayer remounts with a fresh foil; clear
  //   scratchComplete → card-pop off + confetti unmounts) AND unlock —
  //   leaving the current scroll/zoom untouched. captureLockedRef (not
  //   state) so the handler reads the current lock value synchronously
  //   without a stale closure.
  const [resetKey, setResetKey] = useState(0);
  const captureLockedRef = useRef(false);
  const handleLinifyTap = useCallback(() => {
    if (captureLockedRef.current) {
      captureLockedRef.current = false;
      removeCaptureLock();
      setResetKey((k) => k + 1);
      setScratchComplete(false);
    } else {
      captureLockedRef.current = true;
      addCaptureLock();
    }
  }, []);
  // Belt-and-suspenders: drop any active lock if the tree unmounts.
  useEffect(() => () => { if (captureLockedRef.current) removeCaptureLock(); }, []);

  // ── Unique-viewer beacon ────────────────────────────────────────────
  // The Pages Function server-renders this page and, in doing so, calls
  // /api/scratch-share/:id itself — which is what emits the existing view
  // event. That call happens at the edge, before this browser has run any
  // JS, so it cannot know the viewer id and cannot tell one person's ten
  // opens from ten people's one. It also fires for link-preview bots.
  //
  // So the browser reports separately, once per page, carrying its id. The
  // server emits a DIFFERENT event for the vid-bearing call (see
  // routes/scratchShare.ts) so the historical open count is unaffected.
  //
  // Fire-and-forget: the response is discarded, and any failure is ignored.
  // Nothing the recipient sees depends on this.
  const beaconSentRef = useRef(false);
  useEffect(() => {
    // Guards a re-render / StrictMode's double-invoke within one mount.
    if (beaconSentRef.current) return;
    const id = parseIdFromPath(window.location.pathname);
    if (!id) return;
    beaconSentRef.current = true;

    // ONE report per share, ever — the dedup happens here rather than being
    // reconstructed downstream. Opening a DIFFERENT share reports again.
    if (hasReported(id)) return;

    // Marked only AFTER the server confirms. Marking up front would mean a
    // first view that failed to send (offline, blip, server down) is recorded
    // as reported and then never retried — losing that viewer permanently.
    void fetch(`/api/scratch-share/${id}?vid=${encodeURIComponent(viewerId())}`, {
      headers: { Accept: 'application/json' },
    })
      .then((r) => {
        if (r.ok) markReported(id);
      })
      .catch(() => {
        /* not marked — the next load tries again */
      });
  }, []);

  useEffect(() => {
    // If bootstrap already populated state, there's nothing to do.
    if (state.kind !== 'loading') return;

    const id = parseIdFromPath(window.location.pathname);
    if (!id) {
      setState({ kind: 'not_found' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`/api/scratch-share/${id}`, {
          headers: { Accept: 'application/json' },
        });
        if (cancelled) return;
        if (resp.status === 404) {
          setState({ kind: 'not_found' });
          return;
        }
        if (resp.status === 410) {
          setState({ kind: 'expired' });
          return;
        }
        if (!resp.ok) {
          setState({
            kind: 'error',
            message: `Server returned ${resp.status}`,
          });
          return;
        }
        const data = (await resp.json()) as ShareData;
        setState({ kind: 'loaded', data });
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: (err as Error).message ?? 'Network error',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute the card aspect from API dims when present. Legacy/null →
  // CSS-default (1) takes over. The aspect is also baked into a `style`
  // override on the stage so it can refine the value the Pages function
  // already injected at :root level (covers the case where the function
  // didn't have dims at HTML-render time but the API call returned them
  // later).
  const aspectFromState =
    state.kind === 'loaded' && state.data.width && state.data.height
      ? state.data.width / state.data.height
      : null;
  const stageStyle = aspectFromState
    ? ({ ['--card-aspect' as string]: String(aspectFromState) } as React.CSSProperties)
    : undefined;

  return (
    <>
      <OpenInPreviewApp />

      {state.kind === 'loading' && (
        <div className="stage">
          <TopBrand />
          {/* Foil-themed skeleton — same width/aspect rules as the real
              card, so the slot is fixed before and after the foil paints.
              Zero layout shift. */}
          <div className="card-skeleton" aria-hidden="true" />
          <div className="hint">Loading your scratch-off…</div>
          <BottomCta />
        </div>
      )}

      {state.kind === 'not_found' && (
        <div className="stage">
          <TopBrand />
          <div className="center-state">
            <div>
              <h2>This scratch-off isn't here</h2>
              <p>The link might be wrong, or the share has been removed.</p>
            </div>
          </div>
          <BottomCta />
        </div>
      )}

      {state.kind === 'expired' && (
        <div className="stage">
          <TopBrand />
          <div className="center-state">
            <div>
              <h2>This scratch-off has expired</h2>
              <p>Shares are kept for 120 days. Ask the sender to make a fresh one!</p>
            </div>
          </div>
          <BottomCta />
        </div>
      )}

      {state.kind === 'error' && (
        <div className="stage">
          <TopBrand />
          <div className="center-state">
            <div>
              <h2>Something went wrong</h2>
              <p>{state.message}</p>
            </div>
          </div>
          <BottomCta />
        </div>
      )}

      {state.kind === 'loaded' && (
        <div className="stage" style={stageStyle}>
          {/* New layout (2026-06-10 user-driven redesign):
            *   1. BrandMark — Linify wordmark centered above the card
            *   2. .myo-slot — collapsible container. Empty (max-height
            *      0) at rest; expands to fit the "Make Your Own
            *      Scratch Card" pill the moment scratchComplete fires.
            *      Because it's in the flex column flow, expansion
            *      naturally pushes BrandMark UP — the user-facing
            *      moment they asked for: "When the Make your own
            *      button appears it will push the Linify logo up."
            *   3. .card-frame — the foil card
            *   4. GetAppPill — sky-blue Get App button (the prior
            *      header-cta moved here)
            *   5. Dev trigger (preview hosts only)
            * Header bar + "Scratch off to reveal" hint both removed:
            * the brand-mark above the card carries the brand, the
            * card itself signals "scratch me" (foil texture + the
            * Make Your Own pill arrives as the celebratory reward). */}
          <TopBrand onTap={IS_CAPTURE ? handleLinifyTap : undefined} />
          {!IS_CAPTURE && (
          <div className={`myo-slot ${scratchComplete ? 'visible' : ''}`}>
            <a
              className="make-your-own"
              href={APP_STORE_URL}
              aria-hidden={!scratchComplete}
              tabIndex={scratchComplete ? 0 : -1}
            >
              {/* Sparkle icon — Feather "sparkles" path, single point.
                * Reads as "magic / create something fun" — matches the
                * celebratory moment the pill appears in (post-reveal,
                * over the confetti burst). Filled with currentColor
                * so it inherits the pill's white text color. */}
              <svg
                className="make-your-own-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              </svg>
              <span>Make Your Own Scratch-Off</span>
            </a>
          </div>
          )}

          {/* Wrapper frame — `active` class drives the CSS animation
            * chain that fires once scratching completes:
            *   t=0       onScratchComplete fires
            *   t+200     `.card-frame.active .card` animation begins:
            *             card scale-pops 1.0 → 1.025 → 1.0 (~700ms)
            *             with a soft indigo glow blooming outward
            *             from the card's drop-shadow during the
            *             scale-up, then dissipating outward as it
            *             returns to rest. Reads as "the card came
            *             alive for a beat."
            *   t+0       <Confetti> mounts; 24 colored pieces fall
            *             past the card with staggered launches
            *             (0–400 ms delays, 1.8–2.8 s flight times).
            *             The earlier-launched pieces overlap the
            *             card-pop peak so the burst + pop feel
            *             coordinated.
            *   t+~3200   last confetti piece exits the bottom of
            *             the card-frame's overflow region; card
            *             sits clean as the reveal moment.
            *
            * The streak/halo border animation that used to live
            * here was pulled 2026-06-09 (user feedback: too noisy);
            * the orphaned CardHalo.tsx was deleted 2026-06-10. */}
          <div className={`card-frame ${scratchComplete ? 'active' : ''}`}>
            <ScratchPlayer
              key={resetKey}
              outlineUrl={state.data.outlineUrl}
              colorUrl={state.data.colorUrl}
              initialAspect={aspectFromState}
              onScratchComplete={handleScratchComplete}
              autoFinish={IS_AUTO_FINISH}
              completeThreshold={IS_CAPTURE ? MINIMAL_SCRATCH_THRESHOLD : undefined}
            />
            <Confetti active={scratchComplete} />
          </div>

          <BottomCta />
          {/* Dev-trigger button (`.dev-trigger-btn`) removed 2026-06-10
            * at user request — the reveal animation is stable enough
            * that the scratch-by-hand flow is sufficient for QA. The
            * `IS_DEV_HOST` constant is retained for potential future
            * dev affordances and as documentation of the gating
            * pattern, but is currently unreferenced inside App. */}
        </div>
      )}
    </>
  );
}
